import { t } from '../utils/i18n'
/**
 * PttPanel — Panel Radio PTT (lado operador CAD)
 *
 * Se abre como sidebar izquierdo al hacer clic en el icono Radio del NavSidebar.
 * Permite:
 *   - Llamada individual a cualquier sitio (desde mapa o lista)
 *   - Broadcast por zona geográfica (hasta 4 zonas en Santiago)
 *   - Broadcast global a toda la red
 *   - Ver quién está transmitiendo en tiempo real
 *   - Historial de llamadas recientes
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import { Room, RoomEvent, Track, createLocalAudioTrack } from 'livekit-client'
import { Radio, PhoneCall, PhoneOff, Mic, MicOff, X, Globe, Volume2 } from 'lucide-react'
import { useAuthStore } from '../store/authStore'

// ─── Hook: LiveKit room ────────────────────────────────────────────────────────

function usePttRoom() {
  const roomRef    = useRef(null)
  const trackRef   = useRef(null)
  const [state,    setState]    = useState('idle')      // idle | connecting | connected | transmitting
  const [speakers, setSpeakers] = useState([])          // participantes hablando
  const [error,    setError]    = useState(null)

  const connect = useCallback(async (livekitUrl, token) => {
    if (roomRef.current) await disconnect()
    setState('connecting')
    setError(null)
    try {
      const room = new Room({ adaptiveStream: true, dynacast: true })
      roomRef.current = room

      room.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
        setSpeakers(speakers.map(p => p.identity))
      })
      room.on(RoomEvent.Disconnected, () => {
        setState('idle')
        setSpeakers([])
      })
      room.on(RoomEvent.TrackSubscribed, (track) => {
        if (track.kind === Track.Kind.Audio) {
          const el = track.attach()
          el.style.display = 'none'
          document.body.appendChild(el)
        }
      })
      room.on(RoomEvent.TrackUnsubscribed, (track) => {
        track.detach().forEach(el => el.remove())
      })

      await room.connect(livekitUrl, token)
      setState('connected')
    } catch (e) {
      setError(e.message || 'Error conectando')
      setState('idle')
    }
  }, [])

  const disconnect = useCallback(async () => {
    await stopTransmit()
    if (roomRef.current) {
      roomRef.current.disconnect()
      roomRef.current = null
    }
    setState('idle')
    setSpeakers([])
  }, [])

  const startTransmit = useCallback(async () => {
    const room = roomRef.current
    if (!room || state !== 'connected') return
    try {
      trackRef.current = await createLocalAudioTrack({ echoCancellation: true, noiseSuppression: true })
      await room.localParticipant.publishTrack(trackRef.current)
      setState('transmitting')
    } catch (e) {
      setError('No se pudo acceder al micrófono')
    }
  }, [state])

  const stopTransmit = useCallback(async () => {
    const room = roomRef.current
    if (!room || !trackRef.current) return
    try {
      await room.localParticipant.unpublishTrack(trackRef.current)
      trackRef.current.stop()
      trackRef.current = null
    } catch (_) {}
    if (state === 'transmitting') setState('connected')
  }, [state])

  useEffect(() => () => { disconnect() }, [])

  return { state, speakers, error, connect, disconnect, startTransmit, stopTransmit }
}

// ─── PttButton — botón PTT principal ─────────────────────────────────────────

function PttButton({ state, onStart, onStop }) {
  const active = state === 'transmitting'
  const ready  = state === 'connected'

  return (
    <button
      onPointerDown={e => { e.preventDefault(); ready && onStart() }}
      onPointerUp={e => { e.preventDefault(); active && onStop() }}
      onPointerLeave={e => { active && onStop() }}
      disabled={state === 'idle' || state === 'connecting'}
      style={{
        width: '100%', padding: '18px 0', borderRadius: 10,
        border: 'none', cursor: ready || active ? 'pointer' : 'not-allowed',
        background: active
          ? 'var(--danger)'
          : ready
            ? 'var(--accent)'
            : 'var(--bg-muted)',
        color: ready || active ? '#fff' : 'var(--text-muted)',
        fontSize: 13, fontWeight: 700,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        transition: 'background 0.1s, transform 0.05s',
        transform: active ? 'scale(0.98)' : 'scale(1)',
        userSelect: 'none',
        boxShadow: active ? '0 0 20px rgba(239,68,68,0.4)' : 'none',
      }}
    >
      {active ? <MicOff size={16} /> : <Mic size={16} />}
      {active ? 'Transmitiendo… (suelta para parar)' : ready ? 'Mantén para hablar' : state === 'connecting' ? 'Conectando…' : 'Sin conexión'}
    </button>
  )
}

// ─── CallTarget — botón de llamada a sitio/zona ───────────────────────────────

function CallTarget({ label, icon, color, loading, active, onCall, onHangup }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '8px 10px', borderRadius: 8,
      border: `1px solid ${active ? color : 'var(--border)'}`,
      background: active ? color + '15' : 'var(--bg-root)',
    }}>
      <span style={{ fontSize: 16 }}>{icon}</span>
      <span style={{ flex: 1, fontSize: 12, fontWeight: 500, color: 'var(--text-primary)' }}>{label}</span>
      {active ? (
        <button
          onClick={onHangup}
          style={{
            padding: '4px 10px', borderRadius: 6, border: 'none',
            background: 'var(--danger)', color: '#fff',
            fontSize: 11, fontWeight: 600, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 4,
          }}
        >
          <PhoneOff size={11} /> Colgar
        </button>
      ) : (
        <button
          onClick={onCall}
          disabled={loading}
          style={{
            padding: '4px 10px', borderRadius: 6, border: 'none',
            background: color, color: '#fff',
            fontSize: 11, fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.6 : 1,
            display: 'flex', alignItems: 'center', gap: 4,
          }}
        >
          <PhoneCall size={11} /> {t('Llamar','Call')}
        </button>
      )}
    </div>
  )
}

// ─── PttPanel principal ───────────────────────────────────────────────────────

export default function PttPanel({ open, onClose, callTarget, onCallHandled }) {
  const token = useAuthStore(s => s.token)
  const ptt   = usePttRoom()

  const [zones,       setZones]       = useState([])
  const [activeTarget,setActiveTarget]= useState(null)   // { type, id, name, room, log_id }
  const [callLog,     setCallLog]     = useState([])     // historial
  const [calling,     setCalling]     = useState(null)   // id del target cargando

  // Cargar zonas
  useEffect(() => {
    if (!open || !token) return
    fetch('/api/ptt/zones', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(setZones)
      .catch(() => {})
  }, [open, token])

  // Atender llamada iniciada desde el mapa (callTarget prop)
  useEffect(() => {
    if (!callTarget || !open) return
    handleCallSite(callTarget.id, callTarget.name)
    onCallHandled?.()
  }, [callTarget, open])

  async function handleCallSite(siteId, siteName) {
    setCalling(`site-${siteId}`)
    try {
      const res = await fetch(`/api/ptt/call/site/${siteId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      })
      if (!res.ok) throw new Error('Error al llamar')
      const data = await res.json()
      await ptt.connect(data.livekit_url, data.token)
      setActiveTarget({ type: 'site', id: siteId, name: siteName, room: data.room, log_id: data.log_id })
      setCallLog(p => [{ type: 'site', name: siteName, ts: Date.now() }, ...p.slice(0, 9)])
    } catch (e) {
      console.error(e)
    } finally {
      setCalling(null)
    }
  }

  async function handleCallZone(zoneId, zoneName) {
    setCalling(`zone-${zoneId}`)
    try {
      const res = await fetch(`/api/ptt/call/zone/${zoneId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      })
      if (!res.ok) throw new Error()
      const data = await res.json()
      await ptt.connect(data.livekit_url, data.token)
      setActiveTarget({ type: 'zone', id: zoneId, name: zoneName, room: data.room, log_id: data.log_id })
      setCallLog(p => [{ type: 'zone', name: zoneName, ts: Date.now() }, ...p.slice(0, 9)])
    } catch (e) {
      console.error(e)
    } finally {
      setCalling(null)
    }
  }

  async function handleCallAll() {
    setCalling('all')
    try {
      const res = await fetch('/api/ptt/call/all', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      })
      if (!res.ok) throw new Error()
      const data = await res.json()
      await ptt.connect(data.livekit_url, data.token)
      setActiveTarget({ type: 'all', name: t("Toda la red","All network"), room: data.room, log_id: data.log_id })
      setCallLog(p => [{ type: 'all', name: 'Broadcast global', ts: Date.now() }, ...p.slice(0, 9)])
    } catch (e) {
      console.error(e)
    } finally {
      setCalling(null)
    }
  }

  async function hangup() {
    // Notificar al backend para que detenga el egress y registre duración
    if (activeTarget?.log_id) {
      fetch(`/api/ptt/hangup/${activeTarget.log_id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {})
    }
    await ptt.disconnect()
    setActiveTarget(null)
  }

  const ZONE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4']

  if (!open) return null

  return (
    <div style={{
      width: 280, height: '100%',
      background: 'var(--bg-surface)',
      borderRight: '1px solid var(--border)',
      display: 'flex', flexDirection: 'column',
      flexShrink: 0, overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '10px 14px', borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <Radio size={15} color='var(--accent)' />
        <span style={{ fontSize: 13, fontWeight: 700, flex: 1 }}>Radio PTT</span>
        {activeTarget && (
          <span style={{
            fontSize: 10, padding: '2px 8px', borderRadius: 10,
            background: 'var(--danger-light)', color: 'var(--danger)',
            fontWeight: 700, animation: 'ptt-pulse 1.5s ease-in-out infinite',
          }}>● EN LLAMADA</span>
        )}
        <button onClick={onClose} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--text-muted)', padding: '2px',
        }}><X size={15} /></button>
      </div>

      <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 0 }}>

        {/* Llamada activa */}
        {activeTarget && (
          <div style={{
            margin: 12, padding: 12, borderRadius: 10,
            border: '1px solid var(--danger)',
            background: 'var(--danger-light)',
          }}>
            <div style={{
              fontSize: 11, color: 'var(--danger-text)', fontWeight: 700,
              marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <Volume2 size={12} />
              {activeTarget.name}
            </div>
            {ptt.speakers.length > 0 && (
              <div style={{ fontSize: 10, color: 'var(--danger-text)', marginBottom: 8 }}>
                🔊 Transmitiendo: {ptt.speakers.join(', ')}
              </div>
            )}
            <PttButton
              state={ptt.state}
              onStart={ptt.startTransmit}
              onStop={ptt.stopTransmit}
            />
          </div>
        )}

        {ptt.error && (
          <div style={{
            margin: '0 12px 8px', padding: '8px 12px', borderRadius: 8,
            background: 'var(--danger-light)', color: 'var(--danger-text)', fontSize: 11,
          }}>{ptt.error}</div>
        )}

        {/* Broadcast global */}
        <div style={{ padding: '10px 12px 6px' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {t('Broadcast','Broadcast')}
          </div>
          <CallTarget
            label={t("Toda la red","All network")}
            icon={<Globe size={14} />}
            color="#ef4444"
            loading={calling === 'all'}
            active={activeTarget?.type === 'all'}
            onCall={handleCallAll}
            onHangup={hangup}
          />
        </div>

        {/* Zonas */}
        {zones.length > 0 && (
          <div style={{ padding: '10px 12px 6px' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {t('Zonas','Zones')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {zones.map((z, i) => (
                <CallTarget
                  key={z.id}
                  label={z.name}
                  icon="📍"
                  color={ZONE_COLORS[i % ZONE_COLORS.length]}
                  loading={calling === `zone-${z.id}`}
                  active={activeTarget?.type === 'zone' && activeTarget.id === z.id}
                  onCall={() => handleCallZone(z.id, z.name)}
                  onHangup={hangup}
                />
              ))}
            </div>
          </div>
        )}

        {/* Historial */}
        {callLog.length > 0 && (
          <div style={{ padding: '10px 12px', marginTop: 4, borderTop: '1px solid var(--border)' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {t('Llamadas recientes','Recent calls')}
            </div>
            {callLog.map((c, i) => {
              const mins = Math.floor((Date.now() - c.ts) / 60000)
              const label = mins < 1 ? t('Ahora','Now') : mins < 60 ? `${mins}m` : `${Math.floor(mins/60)}h`
              return (
                <div key={i} style={{
                  display: 'flex', gap: 6, alignItems: 'center',
                  padding: '4px 0', fontSize: 11, color: 'var(--text-secondary)',
                }}>
                  <span style={{ fontSize: 10 }}>
                    {c.type === 'all' ? '🌐' : c.type === 'zone' ? '📍' : '📻'}
                  </span>
                  <span style={{ flex: 1 }}>{c.name}</span>
                  <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{label}</span>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Footer info */}
      <div style={{
        padding: '8px 12px', borderTop: '1px solid var(--border)',
        fontSize: 10, color: 'var(--text-muted)',
        display: 'flex', alignItems: 'center', gap: 4,
      }}>
        <Radio size={10} />
        <span>{t('Solo para emergencias operacionales','Operational emergencies only')}</span>
      </div>

      <style>{`
        @keyframes ptt-pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.5; }
        }
      `}</style>
    </div>
  )
}
